/**
 * ErrorState Component - Comprehensive Error Handling
 * Stage 8: Error Handling & User Experience
 */

import React from 'react';

export type ErrorType = 
  | 'network'
  | 'rate_limit'
  | 'generation_failed'
  | 'upload_failed'
  | 'timeout'
  | 'service_unavailable'
  | 'invalid_input'
  | 'unknown';

export interface ErrorInfo {
  type: ErrorType;
  message: string;
  details?: string;
  statusCode?: number;
  canRetry?: boolean;
  retryAfter?: number; // seconds
  suggestedAction?: string;
}

interface ErrorStateProps {
  error: ErrorInfo;
  onRetry?: () => void;
  onClose?: () => void;
  onUpgrade?: () => void;
  className?: string;
  compact?: boolean;
}

const ErrorIcons: Record<ErrorType, string> = {
  network: '📡',
  rate_limit: '⏰',
  generation_failed: '🎨',
  upload_failed: '☁️',
  timeout: '⏱️',
  service_unavailable: '🔧',
  invalid_input: '⚠️',
  unknown: '❌'
};

const ErrorColors: Record<ErrorType, { bg: string; border: string; text: string; accent: string }> = {
  network: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', accent: 'text-blue-600' },
  rate_limit: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', accent: 'text-yellow-600' },
  generation_failed: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', accent: 'text-red-600' },
  upload_failed: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', accent: 'text-orange-600' },
  timeout: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', accent: 'text-purple-600' },
  service_unavailable: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800', accent: 'text-gray-600' },
  invalid_input: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', accent: 'text-amber-600' },
  unknown: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', accent: 'text-red-600' }
};

export default function ErrorState({ 
  error, 
  onRetry, 
  onClose, 
  onUpgrade, 
  className = '', 
  compact = false 
}: ErrorStateProps) {
  const colors = ErrorColors[error.type];
  const icon = ErrorIcons[error.type];

  const formatRetryTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const getActionButtons = () => {
    const buttons = [];

    // Retry button
    if (error.canRetry && onRetry) {
      buttons.push(
        <button
          key="retry"
          onClick={onRetry}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${colors.accent} hover:bg-opacity-10 hover:bg-current border border-current`}
        >
          {error.retryAfter ? `Retry in ${formatRetryTime(error.retryAfter)}` : 'Retry'}
        </button>
      );
    }

    // Upgrade button for rate limit errors
    if (error.type === 'rate_limit' && onUpgrade) {
      buttons.push(
        <button
          key="upgrade"
          onClick={onUpgrade}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Upgrade for More
        </button>
      );
    }

    // Close button
    if (onClose) {
      buttons.push(
        <button
          key="close"
          onClick={onClose}
          className="px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors"
        >
          Dismiss
        </button>
      );
    }

    return buttons;
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-3 rounded-lg border ${colors.bg} ${colors.border} ${className}`}>
        <span className="text-xl">{icon}</span>
        <div className="flex-1">
          <p className={`text-sm font-medium ${colors.text}`}>{error.message}</p>
          {error.details && (
            <p className={`text-xs mt-1 ${colors.accent}`}>{error.details}</p>
          )}
        </div>
        <div className="flex gap-2">
          {getActionButtons()}
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 rounded-lg border ${colors.bg} ${colors.border} ${className}`}>
      <div className="flex items-start gap-4">
        <div className="text-3xl">{icon}</div>
        
        <div className="flex-1">
          <h3 className={`text-lg font-semibold mb-2 ${colors.text}`}>
            {getErrorTitle(error.type)}
          </h3>
          
          <p className={`text-sm mb-3 ${colors.text}`}>
            {error.message}
          </p>
          
          {error.details && (
            <div className={`text-xs p-3 rounded-md bg-white bg-opacity-50 mb-4 ${colors.accent}`}>
              <strong>Details:</strong> {error.details}
            </div>
          )}
          
          {error.suggestedAction && (
            <div className={`text-sm p-3 rounded-md bg-white bg-opacity-50 mb-4 ${colors.text}`}>
              <strong>Suggestion:</strong> {error.suggestedAction}
            </div>
          )}
          
          {error.statusCode && (
            <div className={`text-xs mb-4 ${colors.accent}`}>
              Status Code: {error.statusCode}
            </div>
          )}
        </div>
      </div>
      
      {getActionButtons().length > 0 && (
        <div className="flex gap-3 mt-4">
          {getActionButtons()}
        </div>
      )}
    </div>
  );
}

function getErrorTitle(type: ErrorType): string {
  switch (type) {
    case 'network':
      return 'Network Connection Error';
    case 'rate_limit':
      return 'Rate Limit Exceeded';
    case 'generation_failed':
      return 'Avatar Generation Failed';
    case 'upload_failed':
      return 'Upload Failed';
    case 'timeout':
      return 'Request Timeout';
    case 'service_unavailable':
      return 'Service Unavailable';
    case 'invalid_input':
      return 'Invalid Input';
    case 'unknown':
    default:
      return 'An Error Occurred';
  }
}

// Utility function to create error info from different error sources
export function createErrorInfo(error: any): ErrorInfo {
  // API Service Error
  if (error.name === 'ApiServiceError') {
    if (error.status === 429) {
      return {
        type: 'rate_limit',
        message: 'You have exceeded your weekly limit of 15 avatar generations.',
        details: error.message,
        statusCode: error.status,
        canRetry: false,
        suggestedAction: 'Wait for the weekly reset or upgrade to get more generations.'
      };
    }
    
    if (error.status >= 500) {
      return {
        type: 'service_unavailable',
        message: 'Our servers are currently experiencing issues.',
        details: error.message,
        statusCode: error.status,
        canRetry: true,
        suggestedAction: 'Please try again in a few minutes.'
      };
    }
    
    if (error.status >= 400) {
      return {
        type: 'invalid_input',
        message: 'There was an issue with your request.',
        details: error.message,
        statusCode: error.status,
        canRetry: false,
        suggestedAction: 'Please check your input and try again.'
      };
    }
  }
  
  // Network/Timeout errors
  if (error.message === 'Request timeout' || error.name === 'TimeoutError') {
    return {
      type: 'timeout',
      message: 'The request took too long to complete.',
      details: 'Avatar generation can take up to 5 minutes.',
      canRetry: true,
      suggestedAction: 'Please try again. Complex avatars may take longer to generate.'
    };
  }
  
  // Network errors
  if (error.message === 'Failed to fetch' || error.name === 'NetworkError') {
    return {
      type: 'network',
      message: 'Unable to connect to our servers.',
      details: 'Please check your internet connection.',
      canRetry: true,
      suggestedAction: 'Check your internet connection and try again.'
    };
  }
  
  // File upload errors
  if (error.message.includes('upload') || error.message.includes('file')) {
    return {
      type: 'upload_failed',
      message: 'Failed to process your uploaded image.',
      details: error.message,
      canRetry: true,
      suggestedAction: 'Try uploading a different image or check the file format and size.'
    };
  }
  
  // Generation-specific errors
  if (error.message.includes('generation') || error.message.includes('avatar')) {
    return {
      type: 'generation_failed',
      message: 'Avatar generation failed.',
      details: error.message,
      canRetry: true,
      suggestedAction: 'Try with a different prompt or simpler description.'
    };
  }
  
  // Default unknown error
  return {
    type: 'unknown',
    message: error.message || 'An unexpected error occurred.',
    details: error.stack ? error.stack.split('\n')[0] : undefined,
    canRetry: true,
    suggestedAction: 'Please try again or contact support if the problem persists.'
  };
}
